import { useState } from "react";
import { Link } from "wouter";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

const faqs = [
  {
    q: "Is patient data secure?",
    a: "Yes. Your data is encrypted in transit and at rest, and access is protected behind secure authentication. PalNote is built with clinical privacy in mind so patient information stays confidential.",
  },
  {
    q: "Can I edit generated notes?",
    a: "Absolutely. Every section of a generated SOAP note is fully editable. PalNote drafts the note for you, but you always have final review and approval before saving to a chart.",
  },
  {
    q: "Does PalNote work with my EMR?",
    a: "PalNote lets you export notes as polished PDFs or copy clean, formatted text directly into your existing EMR or practice management system in a couple of clicks.",
  },
  {
    q: "Which healthcare professionals is PalNote designed for?",
    a: "PalNote is purpose-built for chiropractors, physiotherapists, massage therapists, rehabilitation clinics, and other MSK professionals who document patient visits.",
  },
  {
    q: "How accurate is the AI?",
    a: "PalNote uses advanced speech and language models tuned for clinical conversations, and the Documentation Assistant flags anything missing—so you can quickly verify and trust every note.",
  },
];

export function FaqItem({ q, a, id }: { q: string; a: string; id: string }) {
  const [open, setOpen] = useState(false);
  const panelId = `faq-panel-${id}`;
  const buttonId = `faq-button-${id}`;
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white">
      <button
        id={buttonId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className="text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-blue-600 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-6 pb-5 text-sm leading-relaxed text-slate-600">{a}</p>
        </div>
      </div>
    </div>
  );
}

export function FAQSection() {
  return (
    <section id="faq" className="scroll-mt-24 bg-white py-24">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="text-center">
          <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">FAQ</span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Frequently asked questions
          </h2>
        </Reveal>

        <div className="mt-12 space-y-4">
          {faqs.map((faq, i) => (
            <Reveal key={faq.q} delay={i * 0.06}>
              <FaqItem {...faq} id={String(i)} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 text-center">
          <Link
            href="/faq"
            data-testid="link-view-all-faqs"
            className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
          >
            View all frequently asked questions
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
